/**
 * Country ``<select>`` populated from the tenant's enabled delivery
 * countries (``GET /checkout/delivery-countries``).
 *
 * Replaces the legacy free-text country input that lived inline in
 * ``Checkout.tsx`` and ``AccountAddresses.tsx`` and accepted any
 * 2-character string. The tenant admin's whitelist is now the
 * source of truth for what countries the customer can pick.
 *
 * The component handles three loading shapes so callers don't have
 * to:
 *
 *   * ``isPending``  — render a disabled select with a placeholder.
 *   * ``isError``    — render a disabled select with an error label
 *                       (the parent form keeps showing other fields).
 *   * Empty list     — render a disabled select with a "tenant has
 *                       not configured any countries" hint; the user
 *                       can't pick anything (and the BE would reject
 *                       any submit anyway).
 */

import { useTranslation } from "react-i18next";

import {
  type CheckoutCountry,
  useCheckoutDeliveryCountries,
} from "@/api/queries/checkout/deliveryCountries";
import { pickI18nText } from "@/components/admin/I18nField";

interface CountrySelectProps {
  value: string;
  onChange: (next: string) => void;
  /** ``name`` attribute for native form interop. */
  name?: string;
  required?: boolean;
  /** Test selector for E2E. */
  testId?: string;
  /** Optional ``id`` for ``<label htmlFor>``. */
  id?: string;
  /** Browser autofill token (``"shipping country"`` etc.) — KAN-923. */
  autoComplete?: string;
  className?: string;
  /**
   * KAN-1657 — doručovací adresa: země bez dopravce je vidět, ale nejde
   * vybrat (s poznámkou proč). Fakturační adresa ji vybrat smí.
   */
  shippableOnly?: boolean;
}

export function CountrySelect({
  value,
  onChange,
  name,
  required,
  testId,
  id,
  autoComplete,
  className,
  shippableOnly = false,
}: CountrySelectProps): JSX.Element {
  const { t, i18n } = useTranslation("checkout");
  const { data, isPending, isError } = useCheckoutDeliveryCountries();

  const lang = i18n.language?.startsWith("en") ? "en" : "cs";
  const options = data ?? [];

  const baseClass =
    className ??
    "block w-full rounded border border-border-strong bg-surface px-2 py-1.5 text-sm text-ink";

  if (isPending) {
    return (
      <select
        id={id}
        name={name}
        autoComplete={autoComplete}
        disabled
        className={baseClass}
        data-testid={testId}
      >
        <option>{t("countrySelect.loading", "Načítám…")}</option>
      </select>
    );
  }

  if (isError) {
    return (
      <select
        id={id}
        name={name}
        autoComplete={autoComplete}
        disabled
        className={baseClass}
        data-testid={testId}
      >
        <option>{t("countrySelect.error", "Chyba načítání zemí")}</option>
      </select>
    );
  }

  if (options.length === 0) {
    return (
      <select
        id={id}
        name={name}
        autoComplete={autoComplete}
        disabled
        className={baseClass}
        data-testid={testId}
      >
        <option>
          {t(
            "countrySelect.empty",
            "Provozovatel zatím nenastavil dodací země",
          )}
        </option>
      </select>
    );
  }

  return (
    <select
      id={id}
      name={name}
      autoComplete={autoComplete}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={baseClass}
      data-testid={testId}
    >
      {/* Placeholder when nothing chosen yet — matches the
          required-attribute UX of native selects. */}
      {!value && (
        <option value="" disabled>
          {t("countrySelect.placeholder", "— vyberte —")}
        </option>
      )}
      {options.map((c) => {
        const blocked = shippableOnly && c.shippable === false;
        return (
          <option key={c.code} value={c.code} disabled={blocked}>
            {labelFor(c, lang)}
            {blocked &&
              ` — ${t("countrySelect.notShippable", "zatím sem nedoručujeme")}`}
          </option>
        );
      })}
    </select>
  );
}

function labelFor(c: CheckoutCountry, lang: "cs" | "en"): string {
  // Shared read-fallback: EN name wins when present, else the CS name (never
  // blank). Mirrors every other bilingual display site.
  return `${c.flag_emoji} ${pickI18nText(c.name_cs, c.name_en, lang)}`;
}
